import React, { createContext, useContext, useState, useEffect } from "react";
import { getMockData } from "../data/mockData";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'admin' or 'client'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const storedSession = localStorage.getItem("techoside_session");
    if (storedSession) {
      const session = JSON.parse(storedSession);
      setUser(session.user);
      setUserType(session.userType);
    }
    setLoading(false);
  }, []);

  const adminLogin = async (emailOrUsername, password) => {
    const data = getMockData();
    const admin = data.admins.find(
      (a) =>
        (a.email === emailOrUsername || a.username === emailOrUsername) &&
        a.password === password,
    );

    if (admin) {
      const userData = { ...admin, password: undefined };
      setUser(userData);
      setUserType("admin");
      localStorage.setItem(
        "techoside_session",
        JSON.stringify({ user: userData, userType: "admin" }),
      );
      return { success: true, user: userData };
    }

    return { success: false, error: "Invalid email/username or password" };
  };

  const clientLogin = async (loginId, password) => {
    const data = getMockData();
    const client = data.clients.find(
      (c) =>
        c.loginId === loginId &&
        c.password === password &&
        c.status === "active",
    );

    if (client) {
      const userData = { ...client, password: undefined };
      setUser(userData);
      setUserType("client");
      localStorage.setItem(
        "techoside_session",
        JSON.stringify({ user: userData, userType: "client" }),
      );
      return { success: true, user: userData };
    }

    if (
      data.clients.find((c) => c.loginId === loginId && c.status !== "active")
    ) {
      return {
        success: false,
        error: "Your account is inactive. Please contact support.",
      };
    }

    return { success: false, error: "Invalid Login ID or password" };
  };

  const logout = () => {
    setUser(null);
    setUserType(null);
    localStorage.removeItem("techoside_session");
  };

  const isAdmin = () => userType === "admin";
  const isClient = () => userType === "client";
  const isAuthenticated = () => !!user;

  const value = {
    user,
    userType,
    loading,
    adminLogin,
    clientLogin,
    logout,
    isAdmin,
    isClient,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
